import { createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { fetchApiAuth } from 'core/utils/api';
import { handleError, makeUrl } from 'core/utils/helpers';
import { AdHocRow, DalRow } from './types';

// endDate is optional on both reports: omitted (or equal to date) means the
// single day.
interface DateArg { date: string; endDate?: string | undefined }
interface AdHocDateArg { date: string; endDate?: string | undefined }
interface DalResult { date: string; endDate: string; rows: DalRow[] }
interface AdHocResult { date: string; endDate: string; am: AdHocRow[]; pm: AdHocRow[]; retailAm: AdHocRow[]; retailPm: AdHocRow[] }

// Identifies the range a response belongs to, for the slice's stale-response
// guard: a range change has to invalidate an in-flight single-date fetch too.
export const reportRequestKey = (arg: DateArg): string => `${arg.date}:${arg.endDate ?? ''}`;

/**
 * Trigger a browser download for a CSV blob returned by an export endpoint.
 *
 * @param data     Raw response body (blob/string)
 * @param filename Suggested download filename
 */
const downloadCsv = (data: BlobPart, filename: string) => {
    let url: string | null = null;
    try {
        url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
    } finally {
        // Always release the object URL, even if the click/DOM step threw.
        if (url) {
            window.URL.revokeObjectURL(url);
        }
    }
};

export const fetchDailyAccessList = createAsyncThunk<DalResult, DateArg, { rejectValue: string }>(
    'dailyReports/fetchDailyAccessList',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'GET',
                url: makeUrl('/daily-access-list', { date: payload.date, endDate: payload.endDate }),
            });

            return {
                date: response.data.date as string,
                endDate: response.data.endDate as string,
                rows: response.data.rows as DalRow[],
            };
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: errorMessage });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const fetchAdHocBookings = createAsyncThunk<AdHocResult, AdHocDateArg, { rejectValue: string }>(
    'dailyReports/fetchAdHocBookings',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'GET',
                url: makeUrl('/ad-hoc-bookings', { date: payload.date, endDate: payload.endDate }),
            });

            return {
                date: response.data.date as string,
                endDate: response.data.endDate as string,
                am: response.data.am as AdHocRow[],
                pm: response.data.pm as AdHocRow[],
                retailAm: response.data.retailAm as AdHocRow[],
                retailPm: response.data.retailPm as AdHocRow[],
            };
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: errorMessage });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const exportDailyAccessList = createAsyncThunk(
    'dailyReports/exportDailyAccessList',
    async (payload: { date: string; endDate?: string | undefined }, { rejectWithValue }) => {
        try {
            const { date, endDate } = payload;
            const response = await fetchApiAuth({
                method: 'GET',
                url: makeUrl('/daily-access-list/export', { date, endDate }),
                responseType: 'blob',
            });

            const span = endDate && endDate !== date ? `${date}_to_${endDate}` : date;
            downloadCsv(response.data, `daily-access-list-${span}.csv`);
            return true;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: errorMessage });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const exportAdHocBookings = createAsyncThunk(
    'dailyReports/exportAdHocBookings',
    async (payload: { date: string; endDate?: string | undefined; retail?: boolean }, { rejectWithValue }) => {
        try {
            const { date, endDate, retail } = payload;
            const response = await fetchApiAuth({
                method: 'GET',
                url: makeUrl('/ad-hoc-bookings/export', { date, endDate, retail: retail ? 1 : undefined }),
                responseType: 'blob',
            });

            const span = endDate && endDate !== date ? `${date}_to_${endDate}` : date;
            downloadCsv(response.data, `ad-hoc-bookings-${retail ? 'retail-' : ''}${span}.csv`);
            return true;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: errorMessage });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);
